import { Plus, X } from "lucide-react";
import { useState, useEffect } from "react";
import type { FC, ReactNode } from "react";
import type { SectionItem, TableItem, TableGroup } from "./reservations.types";
import StatsBar from "./Components/StatsBar";
import { CheckIcon, MergeIcon, SplitIcon, TableGridIcon, WarningIcon } from "./Icons";
import SectionCard from "./Components/SectionCard";
import Spinner from "./Components/Spinner";
import { useAuth } from "../Context/AuthContext";
import { useParams } from "react-router-dom";
import * as tablesApi from "../lib/api/tables.apis";

type ModalType = 
  | { kind: "addSection" }
  | { kind: "editSection"; section: SectionItem }
  | { kind: "addTable"; sectionId: string }
  | { kind: "editTable"; table: TableItem }
  | { kind: "mergeTable"; sectionId: string }
  | { kind: "deleteSection"; section: SectionItem }
  | { kind: "deleteTable"; table: TableItem }
  | null;

// ─── SHARED UI ──────────────────────────────────────────────────────────────
const FieldLabel: FC<{ children: ReactNode; required?: boolean }> = ({ children, required }) => (
  <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
    {children}{required && <span className="text-orange-400 ml-0.5">*</span>}
  </p>
);

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all placeholder-gray-300 box-border";
const textareaCls = `${inputCls} resize-y min-h-[80px]`;

const Toggle: FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <div onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0
        ${checked ? "bg-orange-500" : "bg-gray-200"}`}>
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform
        ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </div>
    <span className="text-sm font-medium text-stone-700">{label}</span>
  </label>
);

// ─── MODAL WRAPPER ───────────────────────────────────────────────────────────
const Modal: FC<{ title: string; subtitle?: string; onClose: () => void; children: ReactNode; width?: string }> = ({ title, subtitle, onClose, children, width = "max-w-lg" }) => (
  <div className="fixed inset-0 bg-stone-900/40 z-[1000] flex items-center justify-center p-4">
    <div className={`bg-white rounded-2xl w-full ${width} max-h-[90vh] overflow-y-auto shadow-2xl`}>
      <div className="sticky top-0 bg-white z-10 flex items-start justify-between px-7 pt-6 pb-4 border-b border-gray-100 rounded-t-2xl">
        <div>
          <h2 className="m-0 text-lg font-bold text-stone-900">{title}</h2>
          {subtitle && <p className="m-0 mt-0.5 text-xs text-gray-400">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 border-none cursor-pointer rounded-xl p-2 flex items-center text-gray-500 transition-colors mt-0.5 flex-shrink-0">
          <X size={16} />
        </button>
      </div>
      <div className="px-7 py-6">{children}</div>
    </div>
  </div>
);

// ─── SECTION MODAL ───────────────────────────────────────────────────────────
export interface SectionFormData { name: string; description: string; isActive: boolean; }

export const SectionModal: FC<{
  mode: "add" | "edit";
  initial?: SectionItem;
  onClose: () => void;
  onSave: (data: SectionFormData, id?: string) => Promise<void>;
}> = ({ mode, initial, onClose, onSave }) => {
  const [form, setForm] = useState<SectionFormData>({
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    isActive: initial?.isActive ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError("Section name is required."); return; }
    setLoading(true);
    setError("");
    try {
      await onSave(form, initial?.id);
      onClose();
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={mode === "add" ? "Add New Section" : "Edit Section"}
      subtitle={mode === "edit" ? `Editing: ${initial?.name}` : "Create a new dining section"}
      onClose={onClose}
    >
      <div className="flex flex-col gap-5">
        <div>
          <FieldLabel required>Section Name</FieldLabel>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Rooftop, Garden, Mezzanine"
            className={inputCls} />
        </div>
        <div>
          <FieldLabel>Description</FieldLabel>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Optional notes about this section..."
            className={textareaCls} />
        </div>
        <Toggle checked={form.isActive} onChange={v => setForm(f => ({ ...f, isActive: v }))} label="Section is active" />
        {error && (
          <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">
            <WarningIcon /><span>{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white border-none rounded-xl px-6 py-2.5 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2">
            {loading ? <Spinner /> : mode === "add" ? "Create Section" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── TABLE MODAL ─────────────────────────────────────────────────────────────
interface TableFormData { name: string; capacity: number; sectionId: string; isActive: boolean; notes: string; combinableWith: string[]; }

const TableModal: FC<{
  mode: "add" | "edit";
  initial?: TableItem;
  defaultSectionId?: string;
  sections: SectionItem[];
  allTables: TableItem[];
  onClose: () => void;
  onSave: (data: TableFormData, id?: string) => Promise<void>;
}> = ({ mode, initial, defaultSectionId, sections, allTables, onClose, onSave }) => {
  const [form, setForm] = useState<TableFormData>({
    name: initial?.name ?? "",
    capacity: initial?.capacity ?? 2,
    sectionId: initial?.sectionId ?? defaultSectionId ?? sections[0]?.id ?? "",
    isActive: initial?.isActive ?? true,
    notes: initial?.notes ?? "",
    combinableWith: initial?.combinableWith ?? [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const availableTables = allTables.filter(t => t.id !== initial?.id && t.sectionId === form.sectionId);

  const toggleCombinable = (id: string) => {
    setForm(f => ({
      ...f,
      combinableWith: f.combinableWith.includes(id) ? f.combinableWith.filter(x => x !== id) : [...f.combinableWith, id],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError("Table name is required."); return; }
    if (!form.capacity || form.capacity < 1) { setError("Capacity must be at least 1."); return; }
    setLoading(true);
    setError("");
    try {
      await onSave(form, initial?.id);
      onClose();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={mode === "add" ? "Add New Table" : "Edit Table"}
      subtitle={mode === "edit" ? `Editing: ${initial?.name}` : "Add a table to a section"}
      onClose={onClose}
    >
      <div className="flex flex-col gap-5">
        {/* Name + Capacity */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Table Name</FieldLabel>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. T1, A3, VIP-1" className={inputCls} />
          </div>
          <div>
            <FieldLabel required>Capacity</FieldLabel>
            <input type="number" min={1} max={50} value={form.capacity}
              onChange={e => setForm(f => ({ ...f, capacity: Number(e.target.value) }))}
              className={inputCls} />
          </div>
        </div>

        {/* Section */}
        <div>
          <FieldLabel required>Section</FieldLabel>
          <select value={form.sectionId} onChange={e => setForm(f => ({ ...f, sectionId: e.target.value, combinableWith: [] }))}
            className={inputCls}>
            {sections.filter(s => s.isActive).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Notes */}
        <div>
          <FieldLabel>Notes</FieldLabel>
          <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Near window, corner booth" className={inputCls} />
        </div>

        {/* Combinable with */}
        {availableTables.length > 0 && (
          <div>
            <FieldLabel>Combinable With</FieldLabel>
            <p className="m-0 mb-2.5 text-xs text-gray-400">Select tables in the same section that can be merged with this one.</p>
            <div className="flex flex-wrap gap-2">
              {availableTables.map(t => {
                const selected = form.combinableWith.includes(t.id);
                return (
                  <button key={t.id} onClick={() => toggleCombinable(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-xs font-semibold cursor-pointer transition-all
                      ${selected ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 bg-white text-stone-600 hover:border-orange-300"}`}>
                    {selected && <CheckIcon />}
                    {t.name} <span className="text-gray-400">({t.capacity}p)</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active toggle */}
        <Toggle checked={form.isActive} onChange={v => setForm(f => ({ ...f, isActive: v }))} label="Table is active" />

        {error && (
          <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">
            <WarningIcon /><span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white border-none rounded-xl px-6 py-2.5 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2">
            {loading ? <Spinner /> : mode === "add" ? "Add Table" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── MERGE MODAL ─────────────────────────────────────────────────────────────
const MergeModal: FC<{
  sectionId: string;
  sections: SectionItem[];
  groups: TableGroup[];
  onClose: () => void;
  onMerge: (tableIds: string[], notes: string) => Promise<void>;
  onUnmerge: (groupId: string) => Promise<void>;
}> = ({ sectionId, sections, groups, onClose, onMerge, onUnmerge }) => {
  const section = sections.find(s => s.id === sectionId)!;
  const activeTables = section.tables.filter(t => t.isActive);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sectionGroups = groups.filter(g => g.isActive && g.tableIds.some(tid => section.tables.find(t => t.id === tid)));

  const toggle = (id: string) => setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const handleMerge = async () => {
    if (selectedIds.length < 2) { setError("Select at least 2 tables to merge."); return; }
    setLoading(true);
    setError("");
    try {
      await onMerge(selectedIds, notes);
      onClose();
    } catch {
      setError("Merge failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnmerge = async (groupId: string) => {
    setLoading(true);
    try {
      await onUnmerge(groupId);
      onClose();
    } catch {
      setError("Unmerge failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Merge / Unmerge Tables`} subtitle={`Section: ${section.name}`} onClose={onClose}>
      <div className="flex flex-col gap-6">
        {/* Active merges */}
        {sectionGroups.length > 0 && (
          <div>
            <p className="m-0 mb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Merges</p>
            <div className="flex flex-col gap-2">
              {sectionGroups.map(g => {
                const tableNames = g.tableIds.map(id => section.tables.find(t => t.id === id)?.name ?? id).join(" + ");
                return (
                  <div key={g.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <div>
                      <p className="m-0 text-sm font-bold text-amber-800">{tableNames}</p>
                      {g.notes && <p className="m-0 mt-0.5 text-xs text-amber-600">{g.notes}</p>}
                    </div>
                    <button onClick={() => handleUnmerge(g.id)} disabled={loading}
                      className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer transition-colors disabled:opacity-60">
                      <SplitIcon /> Unmerge
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Create merge */}
        <div>
          <p className="m-0 mb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Create New Merge</p>
          <p className="m-0 mb-3 text-xs text-gray-400">Select 2 or more tables to temporarily merge into one group.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {activeTables.map(t => {
              const inGroup = groups.some(g => g.isActive && g.tableIds.includes(t.id));
              const selected = selectedIds.includes(t.id);
              return (
                <button key={t.id} onClick={() => !inGroup && toggle(t.id)} disabled={inGroup}
                  className={`flex flex-col items-center px-4 py-3 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all min-w-[70px]
                    ${inGroup ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed" :
                      selected ? "border-orange-500 bg-orange-50 text-orange-600" :
                      "border-gray-200 bg-white text-stone-700 hover:border-orange-300"}`}>
                  <span>{t.name}</span>
                  <span className="text-[10px] font-normal mt-0.5 opacity-60">{t.capacity}p</span>
                  {inGroup && <span className="text-[9px] font-normal mt-0.5 text-gray-300">merged</span>}
                </button>
              );
            })}
          </div>
          <div>
            <FieldLabel>Merge Notes</FieldLabel>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Dinner party of 8" className={inputCls} />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">
            <WarningIcon /><span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handleMerge} disabled={selectedIds.length < 2 || loading}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white border-none rounded-xl px-6 py-2.5 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2">
            {loading ? <Spinner /> : <><MergeIcon /> Merge {selectedIds.length >= 2 ? `(${selectedIds.length})` : ""}</>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── DELETE CONFIRM MODAL ────────────────────────────────────────────────────
const ConfirmDeleteModal: FC<{
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}> = ({ title, message, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await onConfirm(); onClose(); }
    catch { /* handle */ }
    finally { setLoading(false); }
  };
  return (
    <Modal title={title} onClose={onClose} width="max-w-sm">
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3 bg-red-50 rounded-2xl p-4">
          <div className="text-red-500 flex-shrink-0 mt-0.5"><WarningIcon /></div>
          <p className="m-0 text-sm text-red-700 leading-relaxed">{message}</p>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors">
            Cancel
          </button>
          <button onClick={handle} disabled={loading}
            className="bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white border-none rounded-xl px-5 py-2.5 text-sm font-bold cursor-pointer transition-colors flex items-center gap-2">
            {loading ? <Spinner /> : "Archive"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const TableManagementTab: FC = () => {
  const { accessToken } = useAuth();
  const { restaurantId = "" } = useParams<{ restaurantId: string }>();

  const [sections, setSections] = useState<SectionItem[]>([]);
  const [groups, setGroups] = useState<TableGroup[]>([]);
  const [modal, setModal] = useState<ModalType>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);

  const allTables = sections.flatMap(s => s.tables);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    if (!restaurantId) {
      setInitialError("Missing restaurant id.");
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    setInitialError(null);

    try {
      const [apiSections, apiTables, apiGroups] = await Promise.all([
        tablesApi.listSections(restaurantId, { token: accessToken }),
        tablesApi.listTables(restaurantId, undefined, { token: accessToken }),
        tablesApi.listTableGroups(restaurantId, { active: "all" }, { token: accessToken }),
      ]);

      const nextSections: SectionItem[] = apiSections.map(s => ({
        id: s.id,
        name: s.name,
        description: s.description,
        isActive: s.isActive,
        tables: [],
      }));

      const sectionMap = new Map(nextSections.map(s => [s.id, s] as const));

      for (const t of apiTables) {
        const table: TableItem = {
          id: t.id,
          name: t.name,
          capacity: t.capacity,
          sectionId: t.sectionId,
          isActive: t.isActive,
          notes: t.notes,
          combinableWith: t.combinableWith,
        };
        sectionMap.get(table.sectionId)?.tables.push(table);
      }

      for (const s of nextSections) {
        s.tables.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      }

      setSections(nextSections);
      setGroups(apiGroups.map(g => ({ id: g.id, tableIds: g.tableIds, notes: g.notes, isActive: g.isActive })));
    } catch (error) {
      console.error("Failed to load table management data:", error);
      setInitialError(error instanceof Error ? error.message : "Failed to load table management data.");
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, accessToken]);

  // ── Section CRUD ──────────────────────────────────────────────────────────
  const handleSaveSection = async (data: SectionFormData, id?: string) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    if (id) {
      await tablesApi.updateSection(restaurantId, id, data, { token: accessToken });
      setSections(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));
      showToast(`Section "${data.name}" updated.`);
    } else {
      const created = await tablesApi.createSection(restaurantId, data, { token: accessToken });
      setSections(prev => [...prev, { id: created.id, name: created.name, description: created.description, isActive: created.isActive, tables: [] }]);
      showToast(`Section "${data.name}" created.`);
    }
  };

  const handleDeleteSection = async (section: SectionItem) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    await tablesApi.archiveSection(restaurantId, section.id, { token: accessToken });
    setSections(prev => prev.filter(s => s.id !== section.id));
    showToast(`Section "${section.name}" archived.`);
  };

  // ── Table CRUD ────────────────────────────────────────────────────────────
  const handleSaveTable = async (data: TableFormData, id?: string) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    if (id) {
      const updated = await tablesApi.updateTable(restaurantId, id, data, { token: accessToken });

      setSections(prev => {
        let moved: TableItem | null = null;

        const removedOrUpdated = prev.map(section => {
          const existing = section.tables.find(t => t.id === id);
          if (!existing) return section;

          const nextTable: TableItem = {
            ...existing,
            name: updated.name,
            capacity: updated.capacity,
            sectionId: updated.sectionId,
            isActive: updated.isActive,
            notes: updated.notes,
            combinableWith: updated.combinableWith,
          };

          if (nextTable.sectionId !== section.id) {
            moved = nextTable;
            return { ...section, tables: section.tables.filter(t => t.id !== id) };
          }

          return { ...section, tables: section.tables.map(t => (t.id === id ? nextTable : t)) };
        });

        if (!moved) return removedOrUpdated;

        return removedOrUpdated.map(section => {
          if (section.id !== moved.sectionId) return section;
          return {
            ...section,
            tables: [...section.tables, moved].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
          };
        });
      });

      showToast(`Table "${updated.name}" updated.`);
    } else {
      const created = await tablesApi.createTable(restaurantId, data, { token: accessToken });
      setSections(prev => prev.map(s => s.id === data.sectionId
        ? {
          ...s,
          tables: [...s.tables, {
            id: created.id,
            name: created.name,
            capacity: created.capacity,
            sectionId: created.sectionId,
            isActive: created.isActive,
            notes: created.notes,
            combinableWith: created.combinableWith,
          }].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        }
        : s
      ));
      showToast(`Table "${data.name}" added.`);
    }
  };

  const handleDeleteTable = async (table: TableItem) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    await tablesApi.archiveTable(restaurantId, table.id, { token: accessToken });
    setSections(prev => prev.map(s => ({
      ...s,
      tables: s.tables.filter(t => t.id !== table.id),
    })));
    showToast(`Table "${table.name}" archived.`);
  };

  // ── Merge / Unmerge ───────────────────────────────────────────────────────
  const handleMerge = async (tableIds: string[], notes: string) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    const created = await tablesApi.createTableGroup(restaurantId, { tableIds, notes }, { token: accessToken });
    setGroups(prev => [...prev, { id: created.id, tableIds: created.tableIds, notes: created.notes, isActive: created.isActive }]);
    showToast(`${tableIds.length} tables merged successfully.`);
  };

  const handleUnmerge = async (groupId: string) => {
    if (!restaurantId) throw new Error("Missing restaurant id.");

    const updated = await tablesApi.unmergeTableGroup(restaurantId, groupId, { token: accessToken });
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, isActive: updated.isActive, tableIds: updated.tableIds, notes: updated.notes } : g));
    showToast("Tables unmerged.");
  };

  if (initialLoading) {
    return (
      <div className="p-8 bg-slate-100 min-h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (initialError) {
    return (
      <div className="p-8 bg-slate-100 min-h-full">
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <p className="m-0 text-sm text-red-600 font-semibold">{initialError}</p>
          <button
            onClick={loadData}
            className="mt-4 bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-5 py-2.5 text-sm font-bold cursor-pointer transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-8 bg-slate-100 min-h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold text-slate-900">Table Management</h1>
          <p className="m-0 mt-1.5 text-sm text-slate-500">Manage sections, tables, merges, and capacity</p>
        </div>
        <button onClick={() => setModal({ kind: "addSection" })}
          className="flex items-center gap-1 md:gap-2 bg-orange-500 hover:bg-orange-600 text-white border-none rounded-2xl px-2 md:px-5 py-3 text-sm font-bold cursor-pointer transition-colors shadow-sm">
          <Plus size={16} /> <span className="hidden md:inline">Add </span> Section
        </button>
      </div>

      {/* Stats */}
      <StatsBar sections={sections} groups={groups} />

      {/* Sections list */}
      {sections.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 flex flex-col items-center text-gray-300">
          <div className="mb-4"><TableGridIcon /></div>
          <p className="m-0 text-base font-semibold">No sections yet</p>
          <p className="m-0 mt-1 text-sm">Create your first section to start adding tables.</p>
          <button onClick={() => setModal({ kind: "addSection" })}
            className="mt-5 bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-6 py-2.5 text-sm font-bold cursor-pointer transition-colors">
            + Add Section
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {sections.map(section => (
            <SectionCard key={section.id} section={section} groups={groups} allTables={allTables}
              onEditSection={() => setModal({ kind: "editSection", section })}
              onDeleteSection={() => setModal({ kind: "deleteSection", section })}
              onAddTable={() => setModal({ kind: "addTable", sectionId: section.id })}
              onEditTable={t => setModal({ kind: "editTable", table: t })}
              onDeleteTable={t => setModal({ kind: "deleteTable", table: t })}
              onMerge={() => setModal({ kind: "mergeTable", sectionId: section.id })}
            />
          ))}
        </div>
      )}

      {/* ── MODALS ──────────────────────────────────────────────────────────── */}
      {modal?.kind === "addSection" && (
        <SectionModal mode="add" onClose={() => setModal(null)} onSave={handleSaveSection} />
      )}
      {modal?.kind === "editSection" && (
        <SectionModal mode="edit" initial={modal.section} onClose={() => setModal(null)} onSave={handleSaveSection} />
      )}
      {modal?.kind === "addTable" && (
        <TableModal mode="add" defaultSectionId={modal.sectionId} sections={sections} allTables={allTables} onClose={() => setModal(null)} onSave={handleSaveTable} />
      )}
      {modal?.kind === "editTable" && (
        <TableModal mode="edit" initial={modal.table} sections={sections} allTables={allTables} onClose={() => setModal(null)} onSave={handleSaveTable} />
      )}
      {modal?.kind === "mergeTable" && (
        <MergeModal sectionId={modal.sectionId} sections={sections} groups={groups} onClose={() => setModal(null)} onMerge={handleMerge} onUnmerge={handleUnmerge} />
      )}
      {modal?.kind === "deleteSection" && (
        <ConfirmDeleteModal
          title="Archive Section"
          message={`Are you sure you want to archive "${modal.section.name}"? All ${modal.section.tables.length} tables in this section will also be archived. This action cannot be undone.`}
          onClose={() => setModal(null)}
          onConfirm={() => handleDeleteSection(modal.section)}
        />
      )}
      {modal?.kind === "deleteTable" && (
        <ConfirmDeleteModal
          title="Archive Table"
          message={`Are you sure you want to archive table "${modal.table.name}"? Any active reservations may be affected.`}
          onClose={() => setModal(null)}
          onConfirm={() => handleDeleteTable(modal.table)}
        />
      )}

      {/* ── TOAST ───────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[2000] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-white text-sm font-semibold transition-all
          ${toast.type === "success" ? "bg-stone-900" : "bg-red-500"}`}>
          {toast.type === "success" ? <CheckIcon /> : <WarningIcon />}
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default TableManagementTab;
